/**
 * Offline Storage Service
 * 
 * Provides persistent local storage for the app using Capacitor Preferences.
 * Enables offline functionality - users can view their data without internet.
 * 
 * Architecture:
 * - All user data is cached locally after fetching from server
 * - App works offline using cached data
 * - Changes sync to server when online
 * - Server is source of truth when online
 */

import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';

// Storage keys
const STORAGE_KEYS = {
  // User & Auth
  USER_PROFILE: 'user_profile',
  AUTH_TOKEN: 'auth_token',
  
  // Medical Data
  MEDICATIONS: 'medications',
  ALLERGIES: 'allergies',
  CONDITIONS: 'conditions',
  DOCTORS: 'doctors',
  SURGERIES: 'surgeries',
  VACCINATIONS: 'vaccinations',
  MEDICAL_PROFILE: 'medical_profile',
  EMERGENCY_CONTACTS: 'emergency_contacts',
  INSURANCE: 'insurance',
  
  // Appointments & Reminders
  APPOINTMENTS: 'appointments',
  APPOINTMENT_PREPS: 'appointment_preps',
  MEDICATION_REMINDERS: 'medication_reminders',
  
  // Documents
  DOCUMENTS: 'documents',
  
  // App State
  LAST_SYNC: 'last_sync',
  PENDING_CHANGES: 'pending_changes',
  OFFLINE_QUEUE: 'offline_queue',
};

/**
 * Check if device is online
 */
export const isOnline = async () => {
  try {
    const status = await Network.getStatus();
    return status.connected;
  } catch {
    // Fallback to navigator.onLine for web
    return navigator.onLine;
  }
};

/**
 * Save data to local storage
 */
export const saveLocal = async (key, data) => {
  try {
    await Preferences.set({
      key,
      value: JSON.stringify(data),
    });
    return true;
  } catch (error) {
    console.error(`[OfflineStorage] Failed to save ${key}:`, error);
    return false;
  }
};

/**
 * Get data from local storage
 */
export const getLocal = async (key) => {
  try {
    const { value } = await Preferences.get({ key });
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.error(`[OfflineStorage] Failed to get ${key}:`, error);
    return null;
  }
};

/**
 * Remove data from local storage
 */
export const removeLocal = async (key) => {
  try {
    await Preferences.remove({ key });
    return true;
  } catch (error) {
    console.error(`[OfflineStorage] Failed to remove ${key}:`, error);
    return false;
  }
};

/**
 * Clear all local storage
 */
export const clearAllLocal = async () => {
  try {
    await Preferences.clear();
    return true;
  } catch (error) {
    console.error('[OfflineStorage] Failed to clear storage:', error);
    return false;
  }
};

/**
 * Cache medical data for offline access
 */
export const cacheMedicalData = async (dataType, data) => {
  const key = STORAGE_KEYS[dataType.toUpperCase()];
  if (key) {
    await saveLocal(key, {
      data,
      cachedAt: new Date().toISOString(),
    });
  }
};

/**
 * Get cached medical data
 */
export const getCachedMedicalData = async (dataType) => {
  const key = STORAGE_KEYS[dataType.toUpperCase()];
  if (key) {
    const cached = await getLocal(key);
    return cached?.data || null;
  }
  return null;
};

/**
 * Add change to offline queue (for syncing later)
 */
export const queueOfflineChange = async (change) => {
  const queue = (await getLocal(STORAGE_KEYS.OFFLINE_QUEUE)) || [];
  queue.push({
    ...change,
    queuedAt: new Date().toISOString(),
    id: `change_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  });
  await saveLocal(STORAGE_KEYS.OFFLINE_QUEUE, queue);
};

/**
 * Get pending offline changes
 */
export const getOfflineQueue = async () => {
  return (await getLocal(STORAGE_KEYS.OFFLINE_QUEUE)) || [];
};

/**
 * Clear offline queue after successful sync
 */
export const clearOfflineQueue = async () => {
  await saveLocal(STORAGE_KEYS.OFFLINE_QUEUE, []);
};

/**
 * Update last sync timestamp
 */
export const updateLastSync = async () => {
  await saveLocal(STORAGE_KEYS.LAST_SYNC, new Date().toISOString());
};

/**
 * Get last sync timestamp
 */
export const getLastSync = async () => {
  return await getLocal(STORAGE_KEYS.LAST_SYNC);
};

/**
 * Save user session for offline access
 */
export const saveUserSession = async (user, token) => {
  await saveLocal(STORAGE_KEYS.USER_PROFILE, user);
  if (token) {
    await saveLocal(STORAGE_KEYS.AUTH_TOKEN, token);
  }
};

/**
 * Get saved user session
 */
export const getSavedUserSession = async () => {
  const user = await getLocal(STORAGE_KEYS.USER_PROFILE);
  const token = await getLocal(STORAGE_KEYS.AUTH_TOKEN);
  return { user, token };
};

/**
 * Clear user session on logout
 */
export const clearUserSession = async () => {
  await removeLocal(STORAGE_KEYS.USER_PROFILE);
  await removeLocal(STORAGE_KEYS.AUTH_TOKEN);
  // Keep cached medical data for offline access until explicit clear
};

/**
 * Full logout - clear everything
 */
export const fullLogout = async () => {
  await clearAllLocal();
};

// Listen for network changes
let networkListenerRegistered = false;
const networkChangeCallbacks = [];

export const onNetworkChange = (callback) => {
  networkChangeCallbacks.push(callback);
  
  if (!networkListenerRegistered) {
    Network.addListener('networkStatusChange', (status) => {
      networkChangeCallbacks.forEach(cb => cb(status.connected));
    });
    networkListenerRegistered = true;
  }
  
  // Return unsubscribe function
  return () => {
    const index = networkChangeCallbacks.indexOf(callback);
    if (index > -1) {
      networkChangeCallbacks.splice(index, 1);
    }
  };
};

export const STORAGE_KEYS_EXPORT = STORAGE_KEYS;

export default {
  isOnline,
  saveLocal,
  getLocal,
  removeLocal,
  clearAllLocal,
  cacheMedicalData,
  getCachedMedicalData,
  queueOfflineChange,
  getOfflineQueue,
  clearOfflineQueue,
  updateLastSync,
  getLastSync,
  saveUserSession,
  getSavedUserSession,
  clearUserSession,
  fullLogout,
  onNetworkChange,
  STORAGE_KEYS: STORAGE_KEYS_EXPORT,
};
